import React, { useState, useEffect } from 'react';
import * as invoicesApi from '../api/invoices';

// Utility function to convert ISO date to yyyy-MM-dd format
const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    if (dateString.includes('T')) {
        return dateString.split('T')[0];
    }
    return dateString;
};

const PaymentRecordingModal = ({ 
    isOpen, 
    onClose, 
    invoiceId, 
    paymentSchedule, 
    onPaymentRecorded 
}) => {
    const [formData, setFormData] = useState({
        amount: '',
        paymentMethod: '',
        datePaid: new Date().toISOString().split('T')[0],
        notes: '',
        referenceNumber: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [activeTab, setActiveTab] = useState('record');
    const [editingPayment, setEditingPayment] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setFormData({
                amount: '',
                paymentMethod: '',
                datePaid: new Date().toISOString().split('T')[0],
                notes: '',
                referenceNumber: ''
            });
            setError('');
            setSuccess('');
            setActiveTab('record');
            setEditingPayment(null);
            loadPaymentData();
        }
    }, [isOpen]);

    const loadPaymentData = async () => {
        if (!invoiceId) return;

        try {
            const [statusResponse, methodsResponse] = await Promise.all([
                invoicesApi.getInvoicePaymentStatus(invoiceId),
                invoicesApi.getPaymentMethods(invoiceId)
            ]);

            // Format all dates in the payment status
            const formattedStatus = {
                ...statusResponse.paymentStatus,
                paymentRecords: statusResponse.paymentStatus.paymentRecords?.map(record => ({
                    ...record,
                    date_paid: formatDateForInput(record.date_paid)
                })) || [],
                scheduleItems: statusResponse.paymentStatus.scheduleItems?.map(item => ({
                    ...item,
                    due_date: formatDateForInput(item.due_date),
                    date_paid: formatDateForInput(item.date_paid)
                })) || []
            };

            setPaymentStatus(formattedStatus);
            setPaymentMethods(methodsResponse.paymentMethods);
        } catch (err) {
            console.error('Error loading payment data:', err);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            if (!formData.amount || parseFloat(formData.amount) <= 0) {
                throw new Error('Payment amount must be greater than zero');
            }
            if (!formData.paymentMethod) {
                throw new Error('Payment method is required');
            }
            if (!formData.datePaid) {
                throw new Error('Payment date is required');
            }

            const paymentData = {
                amount: parseFloat(formData.amount),
                paymentMethod: formData.paymentMethod,
                datePaid: formData.datePaid,
                notes: formData.notes || null,
                referenceNumber: formData.referenceNumber || null
            };

            if (editingPayment) {
                // Update existing payment record (from payment_records table)
                await invoicesApi.updatePaymentRecord(invoiceId, editingPayment.id, paymentData);
                setSuccess('Payment updated successfully!');
            } else {
                // Create new payment record
                await invoicesApi.recordInvoicePayment(invoiceId, paymentData);
                setSuccess('Payment recorded successfully!');
            }
            
            // Refresh payment data
            await loadPaymentData();
            
            // Call the callback to refresh payment data
            if (onPaymentRecorded) {
                onPaymentRecorded();
            }

            // Reset form
            setFormData({
                amount: '',
                paymentMethod: '',
                datePaid: new Date().toISOString().split('T')[0],
                notes: '',
                referenceNumber: ''
            });
            setEditingPayment(null);
            
            // Switch to history tab to show results
            setActiveTab('history');
            
        } catch (err) {
            setError(err.message || 'Failed to save payment');
        } finally {
            setLoading(false);
        }
    };

    const handleEditPayment = (payment) => {
        // Date is already formatted from loadPaymentData
        setFormData({
            amount: payment.amount.toString(),
            paymentMethod: payment.payment_method,
            datePaid: formatDateForInput(payment.date_paid), // Extra safety
            notes: payment.notes || '',
            referenceNumber: payment.reference_number || ''
        });
        setEditingPayment(payment);
        setActiveTab('record');
    };

    const handleCancelEdit = () => {
        setFormData({
            amount: '',
            paymentMethod: '',
            datePaid: new Date().toISOString().split('T')[0],
            notes: '',
            referenceNumber: ''
        });
        setEditingPayment(null);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount || 0);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">Payment Management</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                        disabled={loading}
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Tab Navigation */}
                <div className="flex border-b border-gray-200 mb-6">
                    <button
                        onClick={() => setActiveTab('record')}
                        className={`py-2 px-4 font-medium text-sm ${activeTab === 'record' 
                            ? 'text-blue-600 border-b-2 border-blue-600' 
                            : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        {editingPayment ? 'Edit Payment' : 'Record Payment'}
                    </button>
                    <button
                        onClick={() => setActiveTab('history')}
                        className={`py-2 px-4 font-medium text-sm ${activeTab === 'history' 
                            ? 'text-blue-600 border-b-2 border-blue-600' 
                            : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        Payment History
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                        {error}
                    </div>
                )}

                {success && (
                    <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
                        {success}
                    </div>
                )}

                {/* Tab Content */}
                <div className="tab-content">
                    {activeTab === 'record' && (
                        <div className="space-y-6">
                            {/* Payment Overview */}
                            {paymentStatus && (
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-3">Payment Overview</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="text-center">
                                            <p className="text-sm text-gray-600">Invoice Total</p>
                                            <p className="text-xl font-bold text-gray-900">{formatCurrency(paymentStatus.invoiceTotal)}</p>
                                        </div>
                                        <div className="text-center">
                                            <p className="text-sm text-gray-600">Total Paid</p>
                                            <p className="text-xl font-bold text-green-600">{formatCurrency(paymentStatus.totalPaid)}</p>
                                        </div>
                                        <div className="text-center">
                                            <p className="text-sm text-gray-600">Balance</p>
                                            <p className={`text-xl font-bold ${
                                                paymentStatus.balance > 0 ? 'text-red-600' : 
                                                paymentStatus.balance < 0 ? 'text-blue-600' : 'text-green-600'
                                            }`}>
                                                {formatCurrency(paymentStatus.balance)}
                                            </p>
                                        </div>
                                    </div>
                                    {paymentStatus.isOverpaid && (
                                        <div className="mt-3 p-3 bg-blue-100 border border-blue-300 rounded">
                                            <p className="text-blue-800 font-medium">
                                                Overpayment: {formatCurrency(paymentStatus.overpaymentAmount)}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {editingPayment && (
                                <div className="bg-blue-50 p-4 rounded-lg">
                                    <h3 className="text-lg font-semibold text-blue-800 mb-2">Editing Payment</h3>
                                    <p className="text-blue-700">
                                        Original: {formatCurrency(editingPayment.amount)} via {editingPayment.payment_method} on {new Date(editingPayment.date_paid).toLocaleDateString()}
                                    </p>
                                    <button
                                        onClick={handleCancelEdit}
                                        className="mt-2 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                                    >
                                        Cancel Edit
                                    </button>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-4">
                                {/* Payment Amount */}
                                <div>
                                    <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
                                        Payment Amount *
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-2 text-gray-500">$</span>
                                        <input
                                            type="number"
                                            id="amount"
                                            name="amount"
                                            value={formData.amount}
                                            onChange={handleInputChange}
                                            placeholder="0.00"
                                            step="0.01"
                                            min="0"
                                            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Payment Date */}
                                <div>
                                    <label htmlFor="datePaid" className="block text-sm font-medium text-gray-700 mb-1">
                                        Payment Date *
                                    </label>
                                    <input
                                        type="date"
                                        id="datePaid"
                                        name="datePaid"
                                        value={formData.datePaid}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>

                                {/* Payment Method */}
                                <div>
                                    <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700 mb-1">
                                        Payment Method *
                                    </label>
                                    <select
                                        id="paymentMethod"
                                        name="paymentMethod"
                                        value={formData.paymentMethod}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    >
                                        <option value="">Select payment method</option>
                                        {paymentMethods.map((method) => (
                                            <option key={method} value={method}>{method}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Reference Number */}
                                <div>
                                    <label htmlFor="referenceNumber" className="block text-sm font-medium text-gray-700 mb-1">
                                        Reference Number
                                    </label>
                                    <input
                                        type="text"
                                        id="referenceNumber"
                                        name="referenceNumber"
                                        value={formData.referenceNumber}
                                        onChange={handleInputChange}
                                        placeholder="Check number, transaction ID, etc."
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                {/* Notes */}
                                <div>
                                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                                        Notes
                                    </label>
                                    <textarea
                                        id="notes"
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleInputChange}
                                        rows="3"
                                        placeholder="Additional notes about this payment..."
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                {/* Form Actions */}
                                <div className="flex justify-end space-x-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        disabled={loading}
                                        className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                                    >
                                        {loading ? (editingPayment ? 'Updating...' : 'Recording...') : (editingPayment ? 'Update Payment' : 'Record Payment')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Payment History Tab */}
                    {activeTab === 'history' && paymentStatus && (
                        <div className="space-y-6">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-4">Payment History</h3>
                                
                                {paymentStatus.paymentRecords.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        <p>No payments recorded yet.</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {paymentStatus.paymentRecords.map((record) => (
                                            <div key={record.id} className="border rounded-lg p-4 hover:bg-gray-50">
                                                <div className="flex justify-between items-start mb-2">
                                                    <div>
                                                        <p className="font-medium text-gray-900">{formatCurrency(record.amount)}</p>
                                                        <p className="text-sm text-gray-600">
                                                            {new Date(record.date_paid).toLocaleDateString()} via {record.payment_method}
                                                        </p>
                                                    </div>
                                                    <div className="text-right">
                                                        <button
                                                            onClick={() => handleEditPayment(record)}
                                                            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                                                        >
                                                            Edit
                                                        </button>
                                                        {record.reference_number && (
                                                            <p className="text-sm text-gray-600 mt-1">
                                                                Ref: {record.reference_number}
                                                            </p>
                                                        )}
                                                        <p className="text-xs text-gray-500 mt-1">
                                                            {new Date(record.created_at).toLocaleDateString()}
                                                        </p>
                                                    </div>
                                                </div>
                                                
                                                {record.notes && (
                                                    <p className="text-sm text-gray-600 bg-gray-100 p-2 rounded">
                                                        {record.notes}
                                                    </p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Payment Summary */}
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <h4 className="font-medium text-blue-800 mb-2">Payment Summary</h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                                    <div>
                                        <p className="text-sm text-blue-600">Total Payments</p>
                                        <p className="font-bold text-blue-800">{paymentStatus.paymentSummary.totalPayments}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-blue-600">Total Paid</p>
                                        <p className="font-bold text-blue-800">{formatCurrency(paymentStatus.paymentSummary.totalPaid)}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-blue-600">First Payment</p>
                                        <p className="font-bold text-blue-800">
                                            {paymentStatus.paymentSummary.firstPaymentDate ? 
                                                new Date(paymentStatus.paymentSummary.firstPaymentDate).toLocaleDateString() : 
                                                'N/A'}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-blue-600">Last Payment</p>
                                        <p className="font-bold text-blue-800">
                                            {paymentStatus.paymentSummary.lastPaymentDate ? 
                                                new Date(paymentStatus.paymentSummary.lastPaymentDate).toLocaleDateString() : 
                                                'N/A'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PaymentRecordingModal;